import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { isNil } from 'lodash';
import React, { useState } from 'react';

import { ElevationScroll, ToggleVisibilityContainer } from 'common/components/container';
import { ConnectXLogo, SvgIconState } from 'common/components/image';
import { Grid, Stack, Box } from 'common/components/material';
import {
  NavigationIconButton,
  OrganizationDropdown,
  SearchBar,
} from 'common/components/navigation';
import { NavigationTabEnum } from 'common/enum/NavigationTab.enum';
import { useDrawer, useSession } from 'common/hooks';
import useSetActiveNavigationTab, {
  NavigationState,
} from 'common/hooks/useSetActiveNavigationTab';
import NotificationIcon from 'features/org-root/components/Notifications/NotificationIcon';
import { DrawerIdEnum } from 'features/org-root/enums/drawer-id.enum';
import ApplicationBar from './ApplicationBar';
import GlobalCreateButton from 'features/org-root/components/GlobalCreateButton';
import AskConnectXButton from 'features/org-root/components/ask-connectx-button';
import { FeatureGuard } from 'features/feature-access/components/feature-guard.component';
import { FeaturesEnum } from 'features/feature-access/features.enum';

type DesktopApplicationBarProps = {
  showAllMenuItems?: boolean;
  isLoading?: boolean;
};

const DesktopApplicationBar: React.FC<DesktopApplicationBarProps> = ({
  showAllMenuItems = true,
  isLoading,
}) => {
  const { profile, hasOrganization } = useSession();
  const canShowAllMenuItems = showAllMenuItems && !isLoading && !isNil(profile);
  const { openDrawer } = useDrawer();

  const [activeTab, setActiveTab] = useState<NavigationState>({});
  useSetActiveNavigationTab(setActiveTab);

  const toggleSettingsDrawer = () => {
    if (isLoading) {
      return;
    }
    openDrawer(DrawerIdEnum.SETTINGS, {});
  };

  return (
    <>
      <Box
        sx={{
          '& .MuiAppBar-root': {
            paddingLeft: '72px',
            boxSizing: 'border-box',
          },
        }}
      >
        <ElevationScroll>
          <ApplicationBar>
            <Grid container alignItems="center">
              <Grid container spacing={2} item md={3} flexWrap="nowrap">
                <Grid item display="flex" alignItems="center"></Grid>
                <Grid item display="flex" alignItems="center">
                  <ConnectXLogo />
                </Grid>
                {hasOrganization && canShowAllMenuItems && (
                  <Grid item display="flex" minWidth={0}>
                    <OrganizationDropdown />
                  </Grid>
                )}
              </Grid>

              <Grid item xs={4} md={7}>
                <ToggleVisibilityContainer isVisible={canShowAllMenuItems}>
                  <Stack display="flex" flexDirection="row" gap={1}>
                    <SearchBar />
                    <GlobalCreateButton />
                    <FeatureGuard features={[{ feature: FeaturesEnum.MATCHING_ENGINE }]}>
                      <AskConnectXButton />
                    </FeatureGuard>
                  </Stack>
                </ToggleVisibilityContainer>
              </Grid>

              <Grid
                container
                item
                md={2}
                spacing={2}
                justifyContent="flex-end"
                alignItems="center"
              >
                {hasOrganization && (
                  <Grid item>
                    <ToggleVisibilityContainer isVisible={canShowAllMenuItems}>
                      <NavigationIconButton tooltip="Notifications" isActive={false}>
                        <NotificationIcon />
                      </NavigationIconButton>
                    </ToggleVisibilityContainer>
                  </Grid>
                )}

                <Grid item>
                  <NavigationIconButton
                    onClick={toggleSettingsDrawer}
                    tooltip="Settings"
                    isActive={activeTab.currentTab === NavigationTabEnum.ADMIN}
                  >
                    <SvgIconState
                      ActiveIcon={SettingsOutlinedIcon}
                      DisabledIcon={SettingsOutlinedIcon}
                      isActive={activeTab.currentTab === NavigationTabEnum.ADMIN}
                    />
                  </NavigationIconButton>
                </Grid>
              </Grid>
            </Grid>
          </ApplicationBar>
        </ElevationScroll>
      </Box>
    </>
  );
};

export default DesktopApplicationBar;
